import moment from "moment";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import useQueryParams from "./useQueryParams";

/**
 * Hook para sincronizar filtros do formulário com query params na URL
 * 
 * Funcionalidades:
 * - Adiciona à URL apenas campos modificados (diferentes dos defaultValues)
 * - Remove params vazios (null, undefined, '')
 * - Restaura filtros da URL no mount do componente
 * - Suporta navegação back/forward do browser
 * - Suporta prefixos para evitar conflitos entre múltiplos formulários na mesma URL
 * 
 * @param {Object} options - Opções de configuração
 * @param {Object} options.defaultValues - Valores padrão do formulário
 * @param {Function} options.onFilterChange - Callback chamado quando filtros mudam (opcional)
 * @param {boolean} options.syncOnMount - Se deve sincronizar no mount (default: true)
 * @param {boolean} options.syncOnUrlChange - Se deve sincronizar quando a URL mudar (back/forward) (default: true)
 * @param {boolean} options.syncPagination - Se deve sincronizar paginação (default: false)
 * @param {string} options.queryParamsPrefix - Prefixo para os query params (opcional, ex: "employees_")
 * @param {Object} options.pagination - Configuração de paginação (opcional)
 * @param {string} options.pagination.pageIndexKey - Nome do param de página (default: "pageIndex")
 * @param {string} options.pagination.pageSizeKey - Nome do param de tamanho (default: "pageSize")
 * @param {number} options.pagination.defaultPageSize - Default do pageSize (default: defaultValues[pageSizeKey] || 15)
 * @param {Object} options.fieldSerializers - Serializadores por campo (opcional)
 * @param {Function} options.fieldSerializers.<field>.toUrl - Converte valor do form para valor serializável na URL
 * @param {Function} options.fieldSerializers.<field>.fromUrl - Converte valor vindo da URL para valor do form
 * @returns {Object} Métodos para sincronização de filtros
 */
const useFilterSync = ({ 
  defaultValues = {}, 
  onFilterChange, 
  syncOnMount = true,
  syncOnUrlChange = true,
  syncPagination = false,
  queryParamsPrefix = "",
  pagination = {},
  fieldSerializers = {}
}) => {
  const { setValue, getValues, reset } = useFormContext();
  const location = useLocation();
  const lastAppliedSearchRef = useRef(null);
  const lastSelfPushedSearchRef = useRef(null);

  const pageIndexKey = pagination?.pageIndexKey || "pageIndex";
  const pageSizeKey = pagination?.pageSizeKey || "pageSize";
  const defaultPageSize =
    pagination?.defaultPageSize ??
    defaultValues?.[pageSizeKey] ??
    defaultValues?.pageSize ??
    15;

  const { 
    getQueryParams, 
    setQueryParams, 
    clearQueryParams
  } = useQueryParams();

  const extractSearchFromUrl = (url) => {
    if (!url) return "";
    const idx = url.indexOf("?");
    return idx >= 0 ? url.substring(idx) : "";
  };

  /**
   * Adiciona prefixo aos nomes dos parâmetros
   * @param {Object} params - Parâmetros sem prefixo
   * @returns {Object} Parâmetros com prefixo
   */
  const addPrefixToParams = (params) => {
    if (!queryParamsPrefix) return params;
    
    const prefixedParams = {};
    Object.keys(params).forEach((key) => {
      // Não adiciona prefixo em pageIndex e pageSize se syncPagination for false
      if (!syncPagination && (key === pageIndexKey || key === pageSizeKey)) {
        prefixedParams[key] = params[key];
      } else {
        prefixedParams[`${queryParamsPrefix}${key}`] = params[key];
      }
    });
    return prefixedParams;
  };

  /**
   * Remove prefixo dos nomes dos parâmetros
   * @param {Object} params - Parâmetros com prefixo
   * @returns {Object} Parâmetros sem prefixo
   */
  const removePrefixFromParams = (params) => {
    if (!queryParamsPrefix) return params;
    
    const unprefixedParams = {};
    Object.keys(params).forEach((key) => {
      if (key.startsWith(queryParamsPrefix)) {
        const unprefixedKey = key.substring(queryParamsPrefix.length);
        unprefixedParams[unprefixedKey] = params[key];
      } else if (!syncPagination && (key === pageIndexKey || key === pageSizeKey)) {
        // Mantém pageIndex e pageSize sem prefixo se syncPagination for false
        unprefixedParams[key] = params[key];
      }
    });
    return unprefixedParams;
  };

  const getPaginationParamKey = (key) => {
    if (!syncPagination) return key;
    if (!queryParamsPrefix) return key;
    return `${queryParamsPrefix}${key}`;
  };

  /**
   * Compara valores atuais com defaults e retorna apenas os modificados
   * @param {Object} currentValues - Valores atuais do formulário
   * @returns {Object} Apenas valores diferentes dos defaults
   */
  const getModifiedValues = (currentValues) => {
    const modified = {};

    Object.keys(currentValues).forEach((key) => {
      const serializer = fieldSerializers?.[key];
      const currentRawValue = currentValues[key];
      const defaultRawValue = defaultValues[key];

      const currentValue = serializer?.toUrl ? serializer.toUrl(currentRawValue) : currentRawValue;
      const defaultValue = serializer?.toUrl ? serializer.toUrl(defaultRawValue) : defaultRawValue;

      // Ignora valores vazios
      if (currentValue === null || currentValue === undefined || currentValue === "") {
        return;
      }

      // Para arrays, compara conteúdo
      if (Array.isArray(currentValue)) {
        if (currentValue.length === 0) {
          return;
        }
        
        // Se default também é array, compara valores
        if (Array.isArray(defaultValue)) {
          const areEqual = 
            currentValue.length === defaultValue.length &&
            currentValue.every((val, idx) => val === defaultValue[idx]);
          
          if (!areEqual) {
            modified[key] = currentValue;
          }
        } else if (defaultValue === undefined || defaultValue === null) {
          // Se não tem default, adiciona se tiver valores
          modified[key] = currentValue;
        }
      } else if (moment.isMoment(currentValue)) {
        // Para objetos Moment, sempre adiciona (são sempre diferentes da referência)
        // Serializa para ISO string
        modified[key] = currentValue.toISOString();
      } else {
        // Para valores primitivos, compara diretamente
        if (currentValue !== defaultValue) {
          modified[key] = currentValue;
        }
      }
    });

    return modified;
  };

  /**
   * Sincroniza filtros do formulário para a URL
   * Adiciona apenas valores modificados
   * @param {Object} values - Valores do formulário (opcional, usa getValues() se não fornecido)
   */
  const syncFiltersToUrl = (values) => {
    const formValues = values || getValues();
    const modifiedValues = getModifiedValues(formValues);
    
    // Obtém todos os parâmetros atuais da URL
    const currentParams = getQueryParams();
    
    // Remove parâmetros antigos deste formulário (com o mesmo prefixo)
    const otherParams = {};
    Object.keys(currentParams).forEach((key) => {
      if (queryParamsPrefix) {
        // Se tem prefixo, mantém apenas os que NÃO têm este prefixo
        if (!key.startsWith(queryParamsPrefix)) {
          otherParams[key] = currentParams[key];
        }
      } else {
        // Se não tem prefixo, mantém apenas pageIndex e pageSize se syncPagination for false
        if (!syncPagination && (key === pageIndexKey || key === pageSizeKey)) {
          otherParams[key] = currentParams[key];
        }
      }
    });
    
    // Adiciona novos valores com prefixo
    const prefixedValues = addPrefixToParams(modifiedValues);
    
    // Combina outros parâmetros com os novos valores
    const finalParams = { ...otherParams, ...prefixedValues };
    
    // Se syncPagination for false, mantém pageIndex e pageSize da URL atual
    if (!syncPagination) {
      if (currentParams[pageIndexKey]) {
        finalParams[pageIndexKey] = currentParams[pageIndexKey];
      }
      if (currentParams[pageSizeKey]) {
        finalParams[pageSizeKey] = currentParams[pageSizeKey];
      }
    }
    
    const newUrl = setQueryParams(finalParams, true);
    lastSelfPushedSearchRef.current = extractSearchFromUrl(newUrl);
    lastAppliedSearchRef.current = lastSelfPushedSearchRef.current;
  };

  /**
   * Restaura filtros da URL para o formulário
   * Usa setValue para atualizar campos individuais
   */
  const syncFiltersFromUrl = () => {
    const allParams = getQueryParams();
    const params = removePrefixFromParams(allParams);
    
    // Remove parâmetros de paginação se não deve sincronizar
    const filterParams = { ...params };
    if (!syncPagination) {
      delete filterParams[pageIndexKey];
      delete filterParams[pageSizeKey];
    }
    
    // Monta um objeto único e aplica via reset().
    // Isso evita condição de corrida quando alguns campos ainda não registraram no RHF.
    const nextValues = { ...defaultValues };

    // Se não estamos sincronizando paginação, preserva a paginação atual do form.
    if (!syncPagination) {
      const current = getValues();
      nextValues[pageIndexKey] = current?.[pageIndexKey] ?? defaultValues?.[pageIndexKey] ?? 1;
      nextValues[pageSizeKey] = current?.[pageSizeKey] ?? defaultPageSize;
    }

    Object.keys(filterParams).forEach((key) => {
      if (!(key in defaultValues)) return;

      let value = filterParams[key];

      const serializer = fieldSerializers?.[key];
      if (serializer?.fromUrl) {
        value = serializer.fromUrl(value, {
          key,
          allParams,
          filterParams,
          defaultValue: defaultValues[key],
          currentValues: getValues(),
        });
      }

      // Se o defaultValue é null/undefined e o valor parece ser uma data, converte para Moment
      if ((defaultValues[key] === null || defaultValues[key] === undefined) && typeof value === "string") {
        const dateValue = moment(value);
        if (dateValue.isValid()) {
          value = dateValue;
        }
      }

      nextValues[key] = value;
    });

    reset(nextValues);

    if (onFilterChange) {
      onFilterChange(nextValues);
    }

    return nextValues;
  };

  /**
   * Reseta filtros para valores padrão e limpa URL
   */
  const resetFilters = () => {
    reset(defaultValues);
    
    // Obtém todos os parâmetros atuais
    const currentParams = getQueryParams();
    const paramsToKeep = {};
    
    // Remove apenas os parâmetros deste formulário (com prefixo)
    Object.keys(currentParams).forEach((key) => {
      if (queryParamsPrefix) {
        // Mantém parâmetros que NÃO têm este prefixo
        if (!key.startsWith(queryParamsPrefix)) {
          paramsToKeep[key] = currentParams[key];
        }
      } else {
        // Se não tem prefixo e syncPagination for false, mantém apenas paginação
        if (!syncPagination && (key === pageIndexKey || key === pageSizeKey)) {
          paramsToKeep[key] = currentParams[key];
        }
      }
    });
    
    // Se syncPagination for false, mantém paginação
    if (!syncPagination) {
      if (currentParams[pageIndexKey]) {
        paramsToKeep[pageIndexKey] = currentParams[pageIndexKey];
      }
      if (currentParams[pageSizeKey]) {
        paramsToKeep[pageSizeKey] = currentParams[pageSizeKey];
      }
    }
    
    if (Object.keys(paramsToKeep).length > 0) {
      const newUrl = setQueryParams(paramsToKeep, true);
      lastSelfPushedSearchRef.current = extractSearchFromUrl(newUrl);
      lastAppliedSearchRef.current = lastSelfPushedSearchRef.current;
    } else {
      clearQueryParams();
      lastSelfPushedSearchRef.current = "";
      lastAppliedSearchRef.current = "";
    }

    // Chama callback com valores padrão
    if (onFilterChange) {
      onFilterChange(defaultValues);
    }
  };

  /**
   * Handler para submit do formulário
   * Sincroniza com URL e chama callback
   */
  const handleSubmit = (values) => {
    syncFiltersToUrl(values);
    
    if (onFilterChange) {
      onFilterChange(values);
    }
  };

  /**
   * Handler para reset do formulário
   */
  const handleReset = () => {
    resetFilters();
  };

  /**
   * Atualiza paginação na URL
   * @param {number} pageIndex - Índice da página (1-based)
   * @param {number} pageSize - Tamanho da página (opcional)
   */
  const updatePagination = (pageIndex, pageSize) => {
    const currentParams = getQueryParams();

    const urlPageIndexKey = getPaginationParamKey(pageIndexKey);
    const urlPageSizeKey = getPaginationParamKey(pageSizeKey);
    
    // Adiciona pageIndex apenas se > 1
    if (pageIndex > 1) {
      currentParams[urlPageIndexKey] = pageIndex;
    } else {
      delete currentParams[urlPageIndexKey];
    }
    
    // Adiciona pageSize apenas se fornecido e diferente do default da tela
    if (pageSize !== undefined && pageSize !== defaultPageSize) {
      currentParams[urlPageSizeKey] = pageSize;
    } else {
      delete currentParams[urlPageSizeKey];
    }
    
    const newUrl = setQueryParams(currentParams, true);
    lastSelfPushedSearchRef.current = extractSearchFromUrl(newUrl);
    lastAppliedSearchRef.current = lastSelfPushedSearchRef.current;
  };

  /**
   * Obtém valores de paginação da URL
   * @returns {Object} Objeto com pageIndex e pageSize
   */
  const getPaginationFromUrl = () => {
    const params = getQueryParams();

    const urlPageIndexKey = getPaginationParamKey(pageIndexKey);
    const urlPageSizeKey = getPaginationParamKey(pageSizeKey);
    return {
      pageIndex: params[urlPageIndexKey] || 1,
      pageSize: params[urlPageSizeKey] || defaultPageSize,
    };
  };

  // Sincroniza filtros da URL no mount
  useEffect(() => {
    if (syncOnMount) {
      const params = getQueryParams();
      
      // Verifica se existem parâmetros na URL
      const hasParams = Object.keys(params).length > 0;
      
      if (hasParams) {
        syncFiltersFromUrl();
        lastAppliedSearchRef.current = location.search || "";
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincroniza quando a URL mudar (back/forward, links compartilhados, etc.)
  useEffect(() => {
    if (!syncOnUrlChange) return;

    const search = location.search || "";
    if (lastAppliedSearchRef.current === search) return;

    // Se a mudança foi disparada por nós via setQueryParams, ignora 1 ciclo
    if (lastSelfPushedSearchRef.current === search) {
      lastAppliedSearchRef.current = search;
      return;
    }

    lastAppliedSearchRef.current = search;
    syncFiltersFromUrl();
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    syncFiltersToUrl,
    syncFiltersFromUrl,
    resetFilters,
    // Preferir estes aliases para não conflitar com react-hook-form
    handleFilterSubmit: handleSubmit,
    handleFilterReset: handleReset,
    // Mantidos por compatibilidade
    handleSubmit,
    handleReset,
    updatePagination,
    getPaginationFromUrl,
    getModifiedValues,
  };
};

export default useFilterSync;
